/**
 * riduciImmagine.js — la fotografia si riduce NEL BROWSER prima di partire
 * (Alex, 28/10/2026, riferito: foto cliniche a 3000 px sul lato lungo, copie
 * di documenti a 2400 px, JPEG di qualita' 0,85).
 *
 * Perche' qui e non sul server: dal telefono una fotografia pesa 4-8 MB, e
 * sulla rete di uno studio la differenza fra 6 MB e 0,8 MB e' il tempo che il
 * medico aspetta col telefono in mano. La qualita' resta quella decisa: 3000
 * px bastano a una foto di dermatologia (da confermare col medico, domanda 2
 * della proposta), 2400 px all'OCR di un referto.
 *
 * Effetto voluto: un JPEG rifatto dalla tela non porta i metadati
 * dell'originale (EXIF, compresa la posizione GPS del telefono).
 *
 * Un'immagine che il browser non sa aprire (un HEIC su un browser che non lo
 * legge) non si inventa: riduci() solleva, e chi chiama decide - la
 * fotografia parte com'e' (il server la riconosce dai byte), una pagina di un
 * documento no (il server compone il PDF solo da JPEG e PNG).
 */
export const LATO_FOTO = 3000;
export const LATO_DOCUMENTO = 2400;
export const QUALITA = 0.85;

/** Le misure ridotte: il lato lungo al piu' `lato`, mai ingrandite. */
export function misura(larghezza, altezza, lato) {
    const lungo = Math.max(larghezza, altezza);
    if (!lungo || lungo <= lato) return { larghezza, altezza };
    const k = lato / lungo;
    return { larghezza: Math.round(larghezza * k), altezza: Math.round(altezza * k) };
}

async function decodifica(blob) {
    if (typeof createImageBitmap === 'function') {
        try { return await createImageBitmap(blob, { imageOrientation: 'from-image' }); } catch (e) { /* sotto */ }
    }
    return new Promise((risolvi, rifiuta) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); risolvi(img); };
        img.onerror = () => { URL.revokeObjectURL(url); rifiuta(new Error('immagine non leggibile')); };
        img.src = url;
    });
}

/** Il JPEG ridotto di `blob`, col lato lungo al piu' `lato`. */
export async function riduci(blob, lato) {
    const img = await decodifica(blob);
    const w = img.width || img.naturalWidth;
    const h = img.height || img.naturalHeight;
    if (!w || !h) throw new Error('immagine non leggibile');
    const m = misura(w, h, lato);
    const tela = document.createElement('canvas');
    tela.width = m.larghezza;
    tela.height = m.altezza;
    tela.getContext('2d').drawImage(img, 0, 0, m.larghezza, m.altezza);
    if (typeof img.close === 'function') img.close();
    const fuori = await new Promise((risolvi) => tela.toBlob(risolvi, 'image/jpeg', QUALITA));
    /* La tela si svuota: i pixel di una foto clinica non restano in memoria
       piu' del necessario. */
    tela.width = 0; tela.height = 0;
    if (!fuori) throw new Error('immagine non riducibile');
    return fuori;
}

/* ------------------------------------------------------------------
 * HEIC (proposta telefono-stile-nuovo, «HEIC dall'iPhone»). «Carica» dalla
 * galleria di un iPhone da' spesso un HEIC. La strada e' una sola, qui:
 *   1. se il browser lo apre (Safari su iPhone e Mac lo apre, con
 *      createImageBitmap o con <img>), riduci() lo rifa' JPEG come ogni altra
 *      foto, e parte un JPEG;
 *   2. se il browser non lo apre, NON parte com'e': sul server non c'e' una
 *      libreria che lo legga (niente sharp ne' libheif nel backend, verificato
 *      il 28/10/2026), quindi il server non potrebbe ne' ridurlo ne' metterlo
 *      in un PDF, e un HEIC nella galleria non si vedrebbe dai computer della
 *      clinica (Chrome e Firefox non lo aprono). Il file resta in rosso col
 *      motivo (MSG_HEIC_NON_APRIBILE), che dice come fare.
 * Un'altra immagine che il browser non apre (un TIFF, per esempio) parte
 * com'e', come prima: la riconosce il server dai byte.
 * ------------------------------------------------------------------ */
/* Su una riga sola e fra virgolette doppie: il manuale dei medici lo cita
   alla lettera, e verificaManuali lo cerca cosi' com'e'. */
export const MSG_HEIC_NON_APRIBILE = "La foto è in formato HEIC e questo browser non la apre: usa «Scatta», oppure sull'iPhone scegli «Più compatibile» in Impostazioni › Fotocamera › Formati.";

/* Le marche dei contenitori HEIF, le stesse di backend/utils/tipoFile.js. */
const MARCHE_HEIF = ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'];

/** E' un HEIC/HEIF? Dal tipo o dal nome, e se si possono leggere dai primi byte ('ftyp' + la marca). */
export async function eHeic(blob) {
    if (!blob) return false;
    if (/^image\/hei[cf]$/i.test(blob.type || '') || /\.hei[cf]$/i.test(blob.name || '')) return true;
    if (typeof blob.slice !== 'function') return false;
    try {
        const testa = blob.slice(0, 12);
        if (typeof testa.arrayBuffer !== 'function') return false;
        const b = new Uint8Array(await testa.arrayBuffer());
        const t = String.fromCharCode(...b);
        return t.slice(4, 8) === 'ftyp' && MARCHE_HEIF.includes(t.slice(8, 12));
    } catch (e) {
        return false;
    }
}
